import { cn } from '../lib/cn';

/**
 * Фірмова іконка.
 *
 * Емодзі в інтерфейсі не використовуються: вони по-різному малюються на iOS,
 * Android і Windows, не піддаються брендуванню й одразу видають «зроблено
 * нашвидкуруч». Замість них — власний набір із `apps/web/public/brand/icons`,
 * згенерований `scripts/generate-svg-icons.py`.
 *
 * SVG вантажиться як звичайна картинка: не роздуває бандл, кешується
 * браузером і лишається чітким на будь-якому розмірі.
 */

/**
 * Правило набору: ОДНЕ значення — одна іконка, і навпаки.
 *
 * Воно тут не для краси. Коли «Влучність» на арені була зіркою, а в підсумку
 * сесії компасом, два екрани розповідали про те саме різними символами — і
 * жоден із них не запам'ятовувався. А кнопка «Грати» носила схрещені мечі,
 * тобто знак конкретного режиму «Двобій», і не означала нічого.
 */
export const ICON_NAMES = [
  // Предмети
  'subject-math',
  'subject-ukrainian',
  'subject-english',
  'subject-science',
  'subject-logic',
  // Профілі
  'avatar-bohdan',
  'avatar-marko',
  'avatar-parent',
  // Режими складності й складність питання
  'mode-training',
  'mode-showdown',
  'mode-challenge',
  'sprout',
  // Головна дія
  'play',
  // Показники
  'target',
  'checklist',
  // Валюта й нагороди
  'coin',
  'trophy',
  'medal',
  'streak',
  'star',
  // Стани
  'correct',
  'wrong',
  'lock',
  'energy',
  'sync',
  'offline',
  'hourglass',
  'empty',
  'hint',
  'close',
  'compass',
  'fingerprint',
  // Навігація й керування
  'exit',
  'sound-on',
  'sound-off',
  'arrow-left',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export interface IconProps {
  name: IconName;
  /** Сторона в пікселях. За замовчуванням успадковує розмір рядка. */
  size?: number;
  className?: string;
  /** Текстова альтернатива. Якщо не задана — іконка декоративна. */
  label?: string;
  title?: string;
}

export function iconUrl(name: IconName): string {
  return `/brand/icons/${name}.svg`;
}

export function Icon({ name, size, className, label, title }: IconProps): React.JSX.Element {
  return (
    <img
      src={iconUrl(name)}
      alt={label ?? ''}
      title={title}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : 'presentation'}
      width={size}
      height={size}
      draggable={false}
      decoding="async"
      className={cn('inline-block shrink-0 select-none', size ? undefined : 'h-[1em] w-[1em]', className)}
      style={size ? { width: size, height: size } : undefined}
    />
  );
}

/** Іконка предмета за його id — щоб не розписувати мапінг на кожному екрані. */
export const SUBJECT_ICON = {
  math: 'subject-math',
  ukrainian: 'subject-ukrainian',
  english: 'subject-english',
  science: 'subject-science',
  logic: 'subject-logic',
} as const satisfies Record<string, IconName>;

/** Іконка профілю за avatarKey із сервера. */
export const AVATAR_ICON: Record<string, IconName> = {
  bohdan: 'avatar-bohdan',
  marko: 'avatar-marko',
  parent: 'avatar-parent',
};

export function avatarIcon(avatarKey: string): IconName {
  return AVATAR_ICON[avatarKey] ?? 'avatar-parent';
}
